import React, { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "@/components/Toast";
import { getBaseRoute, permissionActions } from "@/lib/utils";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

interface PermissionGuardProps {
  moduleKey: string;
  action?: "add" | "edit" | "view" | string;
  onBackClick?: () => void;
  children: React.ReactNode;
}

const PermissionGuard: React.FC<PermissionGuardProps> = ({
  moduleKey,
  action: actionProp,
  onBackClick,
  children,
}) => {
  const navigate = useNavigate();
  const { action, invoiceAction } = useParams();
  const basePath = getBaseRoute(useLocation().pathname);

  const docAction = invoiceAction;

  // Pick up action from props, fallback to URL parameter (:action)
  const currentAction = actionProp || action;
  
  const normalizedKey = moduleKey?.toLowerCase();
  const accessPermission = useSelector((state: RootState) => state.currentData.accessPermission);
  const modulePermission = accessPermission?.[normalizedKey];
  const isPosted = docAction == "post" || docAction == "posted";

  const hasReadPermission = isPosted ? !!modulePermission?.[permissionActions.READ_POSTED] : !!modulePermission?.[permissionActions.READ];
  const hasActionPostedReadPermission = isPosted && currentAction === "view" && !!modulePermission?.[permissionActions.READ_POSTED];
  const hasActionReadPermission = currentAction === "view" && !!modulePermission?.[permissionActions.READ]; 

  const hasActionPermission =
    !currentAction ||
    (currentAction === "add" && !!modulePermission?.[permissionActions.CREATE]) ||
    (currentAction === "edit" && !!modulePermission?.[permissionActions.UPDATE]) ||
    hasActionReadPermission || hasActionPostedReadPermission;

  const isAllowed = hasReadPermission && hasActionPermission;

  useEffect(() => {
    if (!accessPermission) return;

    if (!isAllowed) {
      toast.error("You don't have access");

      if (onBackClick) {
        onBackClick();
      } else {
        navigate(`/${basePath}`);
      }
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [isAllowed, accessPermission]);

  if (!isAllowed) return null; // Prevents flashing of non-permitted UI

  return <>{children}</>;
};

export default PermissionGuard;